import { Box, Center, Flex, HStack, Text, Tooltip } from '@chakra-ui/react'
import {
  colors,
  getDateString,
  getDateTimeString,
  getFullName,
  getTimeString,
  PopulatedThreadMessage,
  SelectedMessage,
} from '@hb/shared'
import { useEffect, useMemo, useState } from 'react'

import { getRightJustified, useApp, useAuth, useCachedUser } from '@hb/react'
import React from 'react'
import { ProfilePicturePopover } from '../../../Admins/ProfilePicturePopover'
import { Reactions } from '../../../Reactions'
import { ParsedText } from '../../../shared/ParsedText'
import { reportMessageRead } from '../../utils'
import { AttachedFiles } from './AttachedFiles'
import { MessageLinkPreview } from './MessageLinkPreview'
import { MessageReplyToView } from './MessageReplyToView'
import { ReadByPopover } from './ReadByPopover'
import { ThreadMessageMenu } from './ThreadMessageMenu'

export const ThreadMessageView = React.memo(
  ({
    message,
    horizontal,
    withUnreadDivider,
    isPreview,
    scrollToMessage,
    selected,
  }: {
    message: PopulatedThreadMessage
    withUnreadDivider?: boolean
    scrollToMessage: (message: SelectedMessage) => boolean
    selected?: boolean
    horizontal?: boolean
    isPreview?: boolean
  }) => {
    const { data: user, loading } = useCachedUser(message.createdBy)
    const { appName } = useApp()
    const {
      sentAtSameDateAsPrevious,
      sentByMe,
      sentBySameAsPrevious,
      replyTo,
      attachedFiles,
      id,
      reactions,
      text,
    } = message

    const me = useAuth(s => s.authUser)

    const [hovered, setHovered] = useState(false)
    const rightJustified = getRightJustified(appName, message)
    const alignVert = rightJustified ? 'flex-end' : 'flex-start'
    let userName = user ? getFullName(user) : 'Loading sender name...'
    if (!loading && !user) {
      userName = 'Deleted User'
    }
    const bubbleAlign = useMemo(() => {
      if (horizontal) return 'center'
      return rightJustified ? 'flex-end' : 'flex-start'
    }, [rightJustified, horizontal])

    // update read status
    useEffect(() => {
      if (!me || isPreview) return
      if (message.createdBy === me.uid) return
      let shouldUpdate = !message.readBy?.includes(me.uid)
      if (message.editedOn) {
        shouldUpdate = shouldUpdate || !message.editedReadBy?.includes(me.uid)
      }
      if (shouldUpdate) {
        reportMessageRead(message)
      }
    }, [message, me, isPreview])

    return (
      <>
        {withUnreadDivider ? (
          <Center
            boxShadow={`inset 0 10px 12px -10px ${colors.green.hex}`}
            borderTop="1px solid #cdcdcd"
            w="100%">
            <Text
              bg="transparent"
              whiteSpace="nowrap"
              fontSize="xs"
              pt={3}
              pb={1}
              color="gray.500"
              fontFamily="hero-new">
              UNREAD MESSAGES
            </Text>
          </Center>
        ) : null}
        {sentAtSameDateAsPrevious || horizontal ? null : (
          <Center w="100%" px={1}>
            <Text whiteSpace="nowrap" fontSize="xs" py={1} color="gray.500" fontFamily="hero-new">
              {getDateString(message.createdOn, 'long')}
            </Text>
          </Center>
        )}
        <Flex
          id={`message-${id}`}
          onMouseLeave={() => setHovered(false)}
          data-id={id}
          minW="0"
          position="relative"
          align={horizontal ? 'center' : alignVert}
          width="100%"
          // flex={horizontal ? 1 : undefined}
          gap={horizontal ? 1 : 0}
          bg={selected ? 'whiteAlpha.500' : 'transparent'}
          boxShadow={selected ? `0 0 6px ${colors.green.hex}` : 'none'}
          flexFlow={horizontal ? 'row' : 'column'}
          py={horizontal ? 0 : '0.4rem'}>
          {!horizontal && (sentBySameAsPrevious || sentByMe) ? null : (
            <HStack
              position="relative"
              flexFlow={rightJustified ? 'row-reverse' : 'row'}
              spacing={1}
              pb={1}
              px={2}>
              {horizontal ? (
                <ProfilePicturePopover noPopover={isPreview} userId={message.createdBy} />
              ) : (
                <HStack pb={message.deletedOn ? 0 : 1}>
                  {message.deletedOn ? null : (
                    <ProfilePicturePopover
                      noPopover={isPreview}
                      size={7}
                      userId={message.createdBy}
                    />
                  )}
                  <Text
                    whiteSpace="nowrap"
                    fontSize={message.deletedOn ? 'sm' : 'md'}
                    color="gray.600"
                    fontFamily="Hero-New">
                    {userName}
                  </Text>
                </HStack>
              )}
            </HStack>
          )}
          <HStack
            justify={rightJustified ? 'flex-end' : 'flex-start'}
            w={horizontal ? 'unset' : '100%'}
            flex={horizontal ? 1 : 'unset'}
            px={2.5}
            minW="0">
            {/* <Tooltip
          // bg={color}
          bg="gray.50"
          // color='white'
          color="gray.600"
          // textShadow={'0px 0px 3px black'}
          placement={rightJustified ? 'top-end' : 'top-start'}
          label={getDateTimeString(message.createdOn, 'long', true)}
          hasArrow
        > */}
            <Box
              bg="white"
              w={horizontal ? '100%' : 'auto'}
              minW={replyTo && !message.deletedOn ? '200px' : '0'}
              borderRadius="12px"
              maxW={horizontal ? '100%' : '70%'}
              onMouseEnter={() => setHovered(true)}
              // minW=""
              position="relative"
              boxShadow={`${rightJustified ? 1 : -1}px 1px 3px rgba(0,0,0,0.3)`}>
              <Flex
                borderRadius="12px"
                py={2}
                gap="0.18rem"
                flexFlow={horizontal ? 'row' : 'column'}
                justify={horizontal ? 'space-between' : 'flex-start'}
                px={3}
                align={bubbleAlign}
                bg={sentByMe ? `${colors.green.hex}22` : '#ffffff'}>
                {replyTo && !isPreview && !horizontal && !message.deletedOn ? (
                  <MessageReplyToView scrollToMessage={scrollToMessage} replyTo={replyTo} />
                ) : null}
                {message.deletedOn || horizontal ? null : (
                  <AttachedFiles
                    messageGroupId={message.messageGroupId}
                    messageId={id}
                    isPreview={isPreview}
                    attachedFiles={attachedFiles}
                  />
                )}
                {isPreview ? null : <MessageLinkPreview message={message} />}
                <Box
                  // flex={horizontal ? 1 : 'unset'}
                  whiteSpace={horizontal ? 'nowrap' : 'pre-wrap'}
                  isTruncated={!!horizontal}
                  lineHeight={1.2}
                  w="100%"
                  textAlign={rightJustified ? 'right' : 'left'}
                  fontSize="sm"
                  color={message.deletedOn ? 'gray.500' : 'gray.800'}
                  userSelect="text"
                  fontStyle={message.deletedOn ? 'italic' : 'normal'}
                  fontFamily="hero-new">
                  {message.deletedOn ? 'Message Deleted' : <ParsedText>{text}</ParsedText>}
                </Box>
                {message.deletedOn ? null : (
                  <HStack
                    spacing={1}
                    height="16px"
                    borderLeft={horizontal ? '1px solid #00000033' : 'none'}
                    pl={horizontal ? 1 : 0}
                    justify={'flex-end'}
                    w={horizontal ? 'auto' : '100%'}
                    // flexFlow={rightJustified ? 'row-reverse' : 'row'}
                  >
                    {message.editedOn ? (
                      <Tooltip
                        placement="bottom"
                        hasArrow
                        bg="gray.50"
                        color="gray.600"
                        label={`Edited ${getDateTimeString(message.editedOn, 'short')}`}>
                        <Text
                          whiteSpace="nowrap"
                          lineHeight={1}
                          fontStyle="italic"
                          color="gray.500"
                          fontSize="xs">
                          Edited
                        </Text>
                      </Tooltip>
                    ) : null}
                    <Text
                      title={getDateTimeString(message.createdOn, 'long')}
                      whiteSpace="nowrap"
                      lineHeight={1}
                      color="gray.500"
                      fontSize="xs">
                      {getTimeString(message.createdOn)}
                    </Text>
                    <ReadByPopover type={message.threadType} message={message} />
                  </HStack>
                )}
                {reactions?.length && !isPreview && !message.deletedOn ? (
                  <Flex
                    left={rightJustified ? '2px' : '-4px'}
                    position="relative"
                    top="6px"
                    justify={rightJustified ? 'flex-end' : 'flex-start'}
                    align="flex-end"
                    w="100%"
                    height="10px">
                    <Reactions
                      threadType={message.threadType}
                      threadId={message.threadId}
                      messageGroupId={message.messageGroupId}
                      messageId={String(message.createdOn)}
                      reactions={reactions && Array.isArray(reactions) ? reactions : []}
                    />
                  </Flex>
                ) : null}
                {hovered && !isPreview && !message.deletedOn ? (
                  <ThreadMessageMenu message={message} rightJustified={rightJustified} />
                ) : null}
              </Flex>
            </Box>
            {/* </Tooltip> */}
          </HStack>
        </Flex>
      </>
    )
  },
)
